import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { AnchorNavigation } from '$partials/ui';
import { Footer, Header, Meta } from '$partials/global';
import { findModuleAnchors, getAllModules } from '$utilities/data';
import ModuleDispatcher from '$partials/modules/dispatcher';
import { useLocale } from '$contexts/locale';
import { useAnchor } from '$contexts/anchor';
import ModuleRelatedCarousel from '$partials/modules/individual/related-carousel';
import { GlobalLayoutProvider, GlobalLayoutContext } from '$contexts/global-layout';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const FaqPage = ({ data, domain }) => {
    const [stylingWhite, setStylingWhite] = useState(true);

    const { translate: t } = useLocale();
    const { setAnchors, anchors } = useAnchor();

    const page = data?.page;
    let anchorModules = [];
    const modules = getAllModules(page);

    useEffect(() => {
        if (modules) {
            anchorModules = anchorModules.concat(findModuleAnchors(modules));
            setAnchors(anchorModules);
        }
    }, []);

    if (!page) { return null; }

    return (
        <>
            <Meta page={page} />

            <main id='main-content'>
                <div className='min-h-screen'>

                    <GlobalLayoutProvider>
                        <GlobalLayoutContext.Consumer>
                            {(global) => (
                                <>
                                    <Header
                                        menu={data?.navigation}
                                        setStylingWhite={setStylingWhite}
                                        stylingWhite={stylingWhite} />

                                    {/* Header */}
                                    <div className='layout-container z-10 relative mt-90 md:mt-160'>
                                        <h1 className='type-style-2 px-10 sm:px-20'>{page?.title}</h1>
                                    </div>

                                    {/* Content */}
                                    {(modules?.length > 0) && (
                                        <section className={`${anchors.length > 0 ? 'mt-20' : 'mt-40'} sm:mt-50 md:mt-60 relative flex flex-col md:flex-row`}>
                                            {anchors.length > 0 && (
                                                <div className='relative md:absolute h-full w-full pointer-events-none'>
                                                    <div
                                                        className='relative z-10'>
                                                        <AnchorNavigation
                                                            anchors={anchors}
                                                            className='px-10 py-10' />
                                                    </div>
                                                </div>
                                            )}
                                            <div
                                                className='md:-mt-30 w-full'>
                                                {modules.map((module, i) => {
                                                    return (
                                                        <ModuleDispatcher
                                                            key={i}
                                                            module={module}
                                                            nextTypename={modules[i + 1]?.__typename}
                                                            parent={'Detail'}
                                                            previousTypename={modules[i - 1]?.__typename} />
                                                    );
                                                })}
                                            </div>
                                        </section>
                                    )}
                                </>
                            )}
                        </GlobalLayoutContext.Consumer>
                    </GlobalLayoutProvider>

                    {/* Related */}
                    {data.relatedArticles?.items?.length > 0 && (
                        <ModuleRelatedCarousel
                            ctaLabel={t('View All')}
                            ctaLink='/resources'
                            items={data.relatedArticles?.items}
                            title={t('Related Resources')} />
                    )}

                    <div className='md:pt-60'>
                        <Footer
                            domain={domain}
                            menu={data?.footer} />
                    </div>
                </div>
            </main>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        footer: PropTypes.shape({
            items: PropTypes.array
        }),
        page: PropTypes.object,
        relatedArticles: PropTypes.object,
        navigation: PropTypes.object
    }),
    domain: PropTypes.string,
};

FaqPage.propTypes = propTypes;

export default FaqPage;
